import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Space } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { PrinterStatus } from '../../services/exports/Constants';

// Value is the `buttons.*` translation key this action renders with.
const Action = {
  Retry: 'retry_connection',
  Configure: 'configure_external',
};

/**
 * The 15 native ESC/POS statuses collapse onto these presentation keys — the key names the
 * `statuses.*` translation block, the type picks the antd tone, and `actions` says which
 * buttons the alert offers. A status missing from here renders nothing, so a status the
 * native app adds later degrades quietly instead of throwing.
 */
const Presentation = {
  [PrinterStatus.Connected]: { key: 'connected', type: 'success', actions: [] },
  [PrinterStatus.PaperNearEnd]: { key: 'paper_near_end', type: 'warning', actions: [] },
  [PrinterStatus.Busy]: { key: 'busy', type: 'info', actions: [] },
  [PrinterStatus.NotConfigured]: { key: 'not_configured', type: 'warning', actions: [Action.Configure] },
  // Tone is decided at render time — offline only counts as an error while auto-print is on.
  [PrinterStatus.Offline]: { key: 'offline', actions: [Action.Retry] },
  [PrinterStatus.Unreachable]: { key: 'offline', actions: [Action.Retry] },
  [PrinterStatus.Timeout]: { key: 'offline', actions: [Action.Retry] },
  [PrinterStatus.CoverOpen]: { key: 'cover_open', type: 'warning', actions: [] },
  [PrinterStatus.PaperEmpty]: { key: 'paper_empty', type: 'warning', actions: [] },
  [PrinterStatus.WrongPaper]: { key: 'wrong_paper', type: 'warning', actions: [Action.Configure] },
  [PrinterStatus.CutterError]: { key: 'cutter_error', type: 'error', actions: [Action.Retry] },
  [PrinterStatus.Overheat]: { key: 'overheat', type: 'warning', actions: [] },
  [PrinterStatus.MechanicalError]: { key: 'error', type: 'error', actions: [Action.Retry] },
  [PrinterStatus.Unrecoverable]: { key: 'error', type: 'error', actions: [Action.Retry] },
  [PrinterStatus.UnknownError]: { key: 'error', type: 'error', actions: [Action.Retry] },
};

// Shown while waiting for the native app to answer a CONNECT_PRINTER message.
const Connecting = { key: 'connecting', type: 'info', actions: [] };

// Used when no status has ever arrived — then the connected boolean is all we have to go on.
const Fallback = {
  connected: { key: 'connected', type: 'success', actions: [] },
  disconnected: { key: 'disconnected', type: 'info', actions: [Action.Retry] },
};

/**
 * Resolves the printer's state to one presentation entry, or null when the status is one
 * this build doesn't know about.
 */
function resolvePresentation({ status, connecting, connected }) {
  if (connecting) {
    return Connecting;
  }

  if (status) {
    return Presentation[status] ?? null;
  }

  return connected ? Fallback.connected : Fallback.disconnected;
}

/**
 * Short label for the printer row's sub-line, e.g. "Offline". Shares the presentation keys
 * with the alert so a row and its alert can never disagree about the printer's state.
 */
export function printerStatusLabelKey(state) {
  const presentation = resolvePresentation(state);

  return presentation && `statuses.${presentation.key}.label`;
}

/**
 * Describes the external printer whenever it is the selected destination. The built-in
 * printer reports no status, so it never gets an alert.
 */
export default function PrinterStatusAlert({
  status,
  connecting,
  connected,
  printerName,
  autoPrint,
  busy,
  onRetry,
  onConfigure,
}) {
  const { t } = useTranslation(null, { keyPrefix: 'Views:PrinterSettings:PrinterSettingsView' });

  const presentation = resolvePresentation({ status, connecting, connected });

  if (!presentation) {
    return null;
  }

  const { actions, key } = presentation;

  // A printer that isn't printing is only an emergency while orders are supposed to print
  // themselves; with auto-print off it is merely information.
  const urgent = key === 'offline' && autoPrint;
  const type = presentation.type ?? (urgent ? 'error' : 'info');
  const suffix = key === 'offline' && !autoPrint ? '_idle' : '';

  const handlers = {
    [Action.Retry]: onRetry,
    [Action.Configure]: onConfigure,
  };

  return (
    <Alert
      type={type}
      showIcon
      icon={connecting ? <LoadingOutlined spin /> : undefined}
      title={t(`statuses.${key}.title${suffix}`)}
      description={
        <Space direction="vertical" size="small">
          <span>{t(`statuses.${key}.description${suffix}`, { name: printerName })}</span>
          {actions.length > 0 && (
            <Space wrap>
              {actions.map((action) => (
                <Button key={action} disabled={busy} onClick={handlers[action]}>
                  {t(`buttons.${action}`)}
                </Button>
              ))}
            </Space>
          )}
        </Space>
      }
    />
  );
}
